import React, { useState, useEffect, useRef } from 'react';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import {
  AiOutlineLike, AiOutlineDislike, AiOutlineCopy, AiFillDislike,
  AiOutlineCheck, AiOutlineSound, AiOutlineReload,
  AiOutlineLeft, AiOutlineRight
} from 'react-icons/ai';
import { Tooltip } from '@mui/material';
import clsx from 'clsx';
import { Message } from './types';
import { Citation } from '../../store/actions/ChatAction';
import { useTranslation } from 'react-i18next';
import MiniLoadingToCheckmark from '../shared/animations/MiniLoadingToCheckmark';
import { PiThumbsUpLight } from 'react-icons/pi';
import { RenderContent } from './ChatContentRenderer';
import ResearchProgressStepper from './ResearchProgressStepper';
import ProfileImage from '../Header/ProfileImage';

const FadeInDownStyles = `
@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}
.fade-in {
  animation: fadeIn 0.5s ease forwards;
}
@keyframes slideIn {
  0% {
    opacity: 0;
    transform: translateY(10px) scale(0.98);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.slide-in {
  animation: slideIn 0.5s ease-out forwards;
}
@keyframes citationReveal {
  0% { opacity: 0; transform: translateY(6px); }
  100% { opacity: 1; transform: translateY(0); }
}
.citation-card-enter {
  animation: citationReveal 0.35s ease-out forwards;
}
`;

const isDocumentUrl = (url: string): boolean => {
  const documentExtensions = ['.pdf', '.docx', '.doc', '.txt'];
  return documentExtensions.some((ext) => url.toLowerCase().endsWith(ext));
};

interface MessageBubbleProps {
  message: Message;
  onLike: (messageId: string) => void;
  onDislike: (messageId: string) => void;
  onCopy: (messageId: string) => void;
  userName: string;
  userEmail?: string;
  profilePicture?: string | null;
  isAutoScroll: boolean;
  scrollToBottom: () => void;
  speechToken: string | null;
  serviceRegion: string | null;
  aiSettings: any;
  onShowCitation?: (citation: Citation) => void;
  onOpenCitation: (url: string) => void;
  onRegenerate?: (messageId: string) => void;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  onLike,
  onDislike,
  onCopy,
  userName,
  userEmail,
  profilePicture,
  isAutoScroll,
  scrollToBottom,
  speechToken,
  serviceRegion,
  aiSettings,
  onShowCitation,
  onOpenCitation,
  onRegenerate,
  setMessages
}) => {
  const { t } = useTranslation();
  const autoScrollRef = useRef(isAutoScroll);
  const prevMsgId = useRef<string | undefined>(undefined);
  const [progressSteps, setProgressSteps] = useState<string[]>([]);
  const [isTextFullyDisplayed, setIsTextFullyDisplayed] = useState(false);
  const [copied, setCopied] = useState(false);

  const [elapsed, setElapsed] = useState(0);
  const finalElapsedRef = useRef(0);
  const pretty = (s: number) =>
    s < 60 ? `${s}s`
           : `${Math.floor(s / 60)}m ${s % 60}s`;

  useEffect(() => {
    autoScrollRef.current = isAutoScroll;
  }, [isAutoScroll]);

  useEffect(() => {
    if (message.id !== prevMsgId.current) {
      prevMsgId.current = message.id;
      setProgressSteps([]);
      setIsTextFullyDisplayed(false);
    }

    if (message.isPreFormatted || message.sender === 'user' || message.disableTypingEffect || !message.text) {
      setIsTextFullyDisplayed(true);
    }
  }, [message]);

  useEffect(() => {
    if (message.sender === 'assistant' && message.isTyping && !message.isFlowDone) {
      const id = setInterval(() => setElapsed((s) => { const next = s + 1; finalElapsedRef.current = next; return next; }), 1_000);
      return () => clearInterval(id);
    }
    // When flow completes, keep the final elapsed value (don't reset to 0)
    if (message.isFlowDone && finalElapsedRef.current > 0) {
      setElapsed(finalElapsedRef.current);
    }
  }, [message.id, message.isTyping, message.isFlowDone]);
  

  useEffect(() => {
    if (!message.progress) return;
    const oldCount = progressSteps.length;
    const incoming = message.progress;
    if (incoming.length > oldCount) {
      const toAdd = incoming.slice(oldCount);
      let i = 0;
      const timer = setInterval(() => {
        setProgressSteps((prev) => [...prev, toAdd[i]]);
        i++;
        if (i >= toAdd.length) clearInterval(timer);
      }, 200);
      return () => clearInterval(timer);
    }
  }, [message.progress]);


  useEffect(() => {
    if (isAutoScroll && isTextFullyDisplayed) {
      scrollToBottom();
    }
  }, [isTextFullyDisplayed, isAutoScroll, scrollToBottom]);

  const speakText = (txt: string) => {
    if (!serviceRegion || !speechToken || !txt) return;
    const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(speechToken, serviceRegion);
    speechConfig.speechSynthesisVoiceName = aiSettings?.AIVoiceID || 'en-US-JennyNeural';
    const audioConfig = SpeechSDK.AudioConfig.fromDefaultSpeakerOutput();
    const synthesizer = new SpeechSDK.SpeechSynthesizer(speechConfig, audioConfig);

    synthesizer.speakTextAsync(
      txt,
      () => {
        synthesizer.close();
      },
      (err) => {
        console.error('TTS error:', err);
        synthesizer.close();
      }
    );
  };

  const handleCopyClick = () => {
    if (message.text) {
      navigator.clipboard.writeText(message.text);
      setCopied(true);
      onCopy(message.id!);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const getUserInitials = (name: string): string => {
    const parts = name.trim().split(' ');
    return parts.map((p) => p[0]).join('').toUpperCase();
  };

  const isUser = message.sender === 'user';

  const handlePrevResponse = () => {
    if (message.responses && message.currentResponseIndex !== undefined) {
      const newIndex = (message.currentResponseIndex - 1 + message.responses.length) % message.responses.length;
      const newResponse = message.responses[newIndex];
      setMessages((prev) =>
        prev.map((m) =>
          m.id === message.id
            ? {
                ...m,
                text: newResponse.text,
                citations: newResponse.citations,
                currentResponseIndex: newIndex,
                isPreFormatted: true,
              }
            : m
        )
      );
    }
  };

  const handleNextResponse = () => {
    if (message.responses && message.currentResponseIndex !== undefined) {
      const newIndex = (message.currentResponseIndex + 1) % message.responses.length;
      const newResponse = message.responses[newIndex];
      setMessages((prev) =>
        prev.map((m) =>
          m.id === message.id
            ? {
                ...m,
                text: newResponse.text,
                citations: newResponse.citations,
                currentResponseIndex: newIndex,
                isPreFormatted: true,
              }
            : m
        )
      );
    }
  };

  return (
    <>
      <style>{FadeInDownStyles}</style>
      <div className={`flex ${isUser ? 'mb-3' : 'mb-4'} max-w-2/3 w-2/3 self-center ${isUser ? 'justify-end' : 'justify-start'}`}>
        {/* PONS logo for AI responses */}
        {!isUser && (
          <Tooltip title="PONS AI" placement="top">
            <div className="mr-2 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 self-start mt-1">
              <img src="/logosvg.svg" alt="PONS" className="w-7 h-7 object-contain" />
            </div>
          </Tooltip>
        )}
        <div
          className={clsx(
            'rounded-lg',
            isUser
              ? 'py-1 px-2.5 bg-white dark:bg-gray-800 shadow-sm max-w-2xl text-black dark:text-white rounded-br-none'
              : 'py-2 px-3 text-gray-900 dark:text-gray-100 rounded-bl-none w-3xl max-w-3xl'
          )}
        >
          {!isUser && !message.isFlowDone && message.isTyping && (
            message.researchPhase ? (
              <ResearchProgressStepper
                currentPhase={message.researchPhase}
                progress={message.researchProgress}
                elapsed={elapsed}
                detail={progressSteps.length > 0 ? progressSteps[progressSteps.length - 1] : undefined}
                thinkingTraces={message.thinkingTraces}
                evaluationDegraded={message.evaluationDegraded}
              />
            ) : (
              <div className="flex items-center gap-2 mb-2">
                <MiniLoadingToCheckmark isLoading={!message.isFlowDone} />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {progressSteps.length > 0 ? progressSteps[progressSteps.length - 1] : `${t('Thinking')}…`}
                </span>
                <span className="text-xs text-gray-400 dark:text-gray-500">{pretty(elapsed)}</span>
              </div>
            )
          )}
          {/* Collapsed stepper after completion — expandable to review trace */}
          {!isUser && message.isFlowDone && message.researchPhase === 'completed' && (
            <ResearchProgressStepper
              currentPhase="completed"
              elapsed={elapsed}
              thinkingTraces={message.thinkingTraces}
              evaluationDegraded={message.evaluationDegraded}
              isCompleted
            />
          )}
          <RenderContent
            text={message.text || ''}
            citations={message.citations || []}
            isLive={!message.isPreFormatted && message.sender === 'assistant' && !message.disableTypingEffect}
            onComplete={() => setIsTextFullyDisplayed(true)}
            onOpenCitation={onOpenCitation}
            synthesisData={message.synthesisData}
          />
          {isUser && message.attachedFiles && message.attachedFiles.length > 0 && (
            <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300">
              <strong className="block mb-2">{t('Attached Files')}</strong>
              <div className="flex flex-wrap gap-2">
                {message.attachedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-2 px-3 py-1 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 border border-gray-300 dark:border-gray-600 shadow-sm hover:shadow-md"
                  >
                    <span className="text-gray-800 dark:text-gray-200">
                      {file.filename.length > 20 ? `${file.filename.slice(0, 17)}...` : file.filename}
                    </span>
                    <button
                      onClick={() => onOpenCitation(file.blob_path)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm"
                    >
                      {t('View')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!isUser && isTextFullyDisplayed && message.citations && message.citations.length > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
              <button
                className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-2 transition-colors"
                onClick={() => {
                  const el = document.getElementById(`citations-panel-${message.id}`);
                  if (el) el.classList.toggle('hidden');
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                  <path fillRule="evenodd" d="M4.25 2A2.25 2.25 0 002 4.25v11.5A2.25 2.25 0 004.25 18h11.5A2.25 2.25 0 0018 15.75V4.25A2.25 2.25 0 0015.75 2H4.25zM6 13.25V3.5h8v9.75a.75.75 0 01-1.28.53L10 11.06l-2.72 2.72A.75.75 0 016 13.25z" clipRule="evenodd" />
                </svg>
                {t('Sources')} ({message.citations.length})
              </button>
              <div id={`citations-panel-${message.id}`} className="space-y-1.5">
                {message.citations.map((c, i) => (
                  <div
                    key={i}
                    id={`citation-${c.number}`}
                    className="citation-card-enter group flex items-start gap-2 p-2 rounded-lg border border-gray-100 dark:border-gray-700/60 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors cursor-pointer"
                    style={{ animationDelay: `${i * 60}ms`, animationFillMode: 'backwards' }}
                    onClick={(e) => {
                      e.preventDefault();
                      if (c.url && isDocumentUrl(c.url)) onOpenCitation(c.url);
                    }}
                  >
                    <span className="flex-shrink-0 w-5 h-5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-[10px] font-bold flex items-center justify-center mt-0.5">
                      {c.number || i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate leading-tight">
                        {c.title || c.law || `Source ${c.number || i + 1}`}
                      </p>
                      {(c.snippet || c.section) && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5 leading-snug">
                          {c.section && <span className="font-medium text-gray-600 dark:text-gray-300">{c.section} · </span>}
                          {c.snippet}
                        </p>
                      )}
                    </div>
                    {c.url && (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 flex-shrink-0 text-gray-300 group-hover:text-blue-500 transition-colors mt-0.5">
                        <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5zm7.25-.75a.75.75 0 01.75-.75h3.5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0V6.31l-5.47 5.47a.75.75 0 01-1.06-1.06l5.47-5.47H12.25a.75.75 0 01-.75-.75z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!isUser && isTextFullyDisplayed && message.isFlowDone && (
            <div className="flex items-center mt-2 space-x-2">
              <Tooltip title={t('Upvote') || ''} placement="top">
                <button
                  className={clsx(
                    'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300',
                    message.liked && 'text-blue-500'
                  )}
                  onClick={() => onLike(message.id!)}
                >
                  {message.liked ? <PiThumbsUpLight size={16} /> : <AiOutlineLike size={16} />}
                </button>
              </Tooltip>
              <Tooltip title={t('Downvote') || ''} placement="top">
                <button
                  className={clsx(
                    'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300',
                    message.disliked && 'text-red-500'
                  )}
                  onClick={() => onDislike(message.id!)}
                >
                  {message.disliked ? <AiFillDislike size={16} /> : <AiOutlineDislike size={16} />}
                </button>
              </Tooltip>
              <Tooltip title={copied ? t('Copied!') : t('Copy to clipboard')} placement="top">
                <button
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  onClick={handleCopyClick}
                >
                  {copied ? <AiOutlineCheck size={16} /> : <AiOutlineCopy size={16} />}
                </button>
              </Tooltip>
              <Tooltip title={t('Listen') || ''} placement="top">
                <button
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  onClick={() => speakText(message.text || '')}
                  disabled={!message.text}
                >
                  <AiOutlineSound size={16} />
                </button>
              </Tooltip>
              {onRegenerate && (
                <Tooltip title={t('Regenerate response')} placement="top">
                  <button
                    className="group relative p-1 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-300"
                    onClick={() => onRegenerate(message.id!)}
                  >
                    <AiOutlineReload
                      size={16}
                      className="transform group-hover:rotate-180 transition-transform duration-500"
                    />
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  </button>
                </Tooltip>
              )}
            </div>
          )}
          {!isUser && message.isFlowDone && message.responses && message.responses.length > 1 && (
            <div className="flex items-center mt-2 space-x-2">
              <button
                onClick={handlePrevResponse}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                disabled={message.currentResponseIndex === 0}
              >
                <AiOutlineLeft size={16} />
              </button>
              <span className="text-xs text-gray-500">
                {message.currentResponseIndex! + 1} / {message.responses.length}
              </span>
              <button
                onClick={handleNextResponse}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                disabled={message.currentResponseIndex === message.responses.length - 1}
              >
                <AiOutlineRight size={16} />
              </button>
            </div>
          )}
        </div>
        {isUser && (
          <div className="ml-2 flex-shrink-0 self-start mt-1">
            <ProfileImage
              profilePicture={profilePicture || null}
              email={userEmail || null}
              initials={getUserInitials(userName)}
              name={userName}
              size="w-8 h-8"
            />
          </div>
        )}
      </div>
    </>
  );
};

export default MessageBubble;
